import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Button, Typography, Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  setZipCode,
  setProviderType,
  setProviderSpecialty,
  setSortCriteria,
  setSortDirection,
  setSearchRadius,
  toggleSortDirection,
  toggleAccessibility,
  setAccessibilities,
  clearSearchInput,
  setGenderFilter,
  setEntityType,
  setSearchClicked,
  resetProviderSearch,
} from '@apps/shared/src/providerSearch/actions';
import { setPlanState } from '@apps/shared/src/planSelect/actions';
import {
  SortCriteria,
  SortDirection,
  SearchRadius,
  Accessibility,
} from '@apps/shared/src/providerSearch/types/providerSearch';
import { allAccessibilities } from '@apps/shared/src/providerSearch/api/providers';
import Sidebar from '../shared/providerSearchSidebar';
import { RootState } from '../store';
import AccessibilityFilter from './filters/AccessibilityFilter';
import PlanSelectFilter from './filters/PlanSelectFilter';
import ProviderTypeFilter from './filters/ProviderTypeFilter';
import SortControl from './filters/SortControl';
import ZipCodeEntry from './filters/ZipCodeEntry';
import SearchBar from './SearchBar';
import SliderSelector from './filters/SliderSelector';
import GenderFilter from './filters/GenderFilter';
import EntityTypeFilter from './filters/EntityTypeFilter';
import { getProviders } from './actions';

const SidebarContainer = styled(Box)({
  padding: '1rem 1rem 4rem 1rem',
  width: 310,
  backgroundColor: '#fafafa',
  overflowY: 'auto',
  overflowX: 'hidden',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  '& > div > *': {
    marginTop: '1.75rem',
  },
  '& > div > *:first-of-type': {
    marginTop: 0,
  },
});

const ResetButton = styled('button')({
  color: '#ff0000',
  textDecoration: 'underline',
  padding: '0',
  border: 'none',
  backgroundColor: 'white',
  cursor: 'pointer',
});

const mapDispatchToProps = {
  setZipCode,
  setProviderType,
  setProviderSpecialty,
  setSortCriteria,
  setSortDirection,
  setSearchRadius,
  toggleAccessibility,
  toggleSortDirection,
  setAccessibilities,
  clearSearchInput,
  setGenderFilter,
  setEntityType,
  setSearchClicked,
  getProviders,
  resetProviderSearch,
  setPlanState,
};

const mapStateToProps = ({ providerSearch, planSelect }: RootState): StateProps => ({
  searchInput: providerSearch.searchInput,
  searchRadius: providerSearch.radius,
  selectedZipCode: providerSearch.selectedZipCode,
  displayPlanMessage: providerSearch.displayPlanMessage,
  planMessage: providerSearch.planMessage,
  selectedProviderType: providerSearch.selectedProviderType,
  selectedProviderSpecialty: providerSearch.selectedProviderSpecialty,
  selectedAccessibilities: providerSearch.selectedAccessibilities,
  providergendercode: providerSearch.providergendercode,
  entitytype: providerSearch.entitytype,
  searchClicked: providerSearch.searchClicked,
  planID: planSelect.selectedPlan?.id,
});

type DispatchProps = {
  setZipCode: typeof setZipCode;
  validateZipCode: (selectedZipCode: string) => void;
  getProviders: (
    planID: string,
    selectedZipCode: string,
    selectedRadius: string,
    searchInput: string,
    limit: number,
    page: number,
    accessibilities: Accessibility[],
    providerType: string,
    providerSpeciality: string,
    gender: string,
    entityType: string
  ) => void;
};

type StateProps = {
  searchInput: string;
  selectedZipCode: string;
  searchRadius: SearchRadius;
  displayPlanMessage: boolean;
  planMessage: string;
  selectedProviderType: string;
  selectedProviderSpecialty: string;
  providergendercode: string;
  entitytype: string;
  searchClicked: boolean;
  planID: any;
  selectedAccessibilities: any[];
};

type Props = StateProps & DispatchProps & typeof mapDispatchToProps;

export function ProviderSearchFilterPane({
  selectedZipCode,
  displayPlanMessage,
  searchInput,
  planMessage,
  searchRadius,
  planID,
  selectedProviderType,
  selectedProviderSpecialty,
  selectedAccessibilities,
  providergendercode,
  entitytype,
  searchClicked,
  getProviders,
  setZipCode,
  setProviderType,
  setProviderSpecialty,
  setSortCriteria,
  setSortDirection,
  setSearchRadius,
  toggleSortDirection,
  setAccessibilities,
  clearSearchInput,
  setGenderFilter,
  setEntityType,
  setSearchClicked,
  resetProviderSearch,
  setPlanState,
}: Props): JSX.Element {
  const [input, setInput] = useState(searchInput);
  const [sliderValue, setSliderValue] = useState<number>(Number(searchRadius));
  const [zipcodeValue, setZipCodeValue] = useState<string>(selectedZipCode);

  const isSearchDisabled = !zipcodeValue || zipcodeValue.length !== 5 || !selectedProviderType;

  const handleSearch = () => {
    setSearchClicked(true);
    if (!isSearchDisabled) {
      getProviders(
        planID,
        zipcodeValue,
        String(sliderValue),
        input,
        50,
        1,
        selectedAccessibilities,
        selectedProviderType,
        selectedProviderSpecialty,
        providergendercode,
        entitytype
      );
    }
  };

  const resetFilter = () => {
    clearSearchInput();
    setInput('');

    // Clear zip code
    setZipCode('');
    setZipCodeValue('');

    // Clear provider type and specialty
    setProviderType('');
    setProviderSpecialty('');

    // Reset sort criteria
    setSortCriteria('accessibility' as SortCriteria);
    setSortDirection('asc');

    // Reset accessibility filters to all
    setAccessibilities(allAccessibilities);

    // Reset slider to default value
    setSliderValue(25);
    setSearchRadius('25' as SearchRadius);

    // Reset gender and entity filters
    setGenderFilter('All');
    setEntityType('All');

    // Clear plan selection
    setPlanState(undefined);

    // Reset search clicked state so user can start a new search
    setSearchClicked(false);

    // Clear provider storage and data arrays
    window.localStorage.removeItem('providerSearch');
    resetProviderSearch();
  };

  useEffect(() => {
    if (selectedZipCode && selectedProviderType) {
      setSearchClicked(true);
      getProviders(
        planID,
        selectedZipCode,
        '25',
        '',
        50,
        1,
        selectedAccessibilities,
        selectedProviderType,
        ''
      );
    }
  }, [searchClicked]);

  return (
    <Sidebar hideButton toggleButtonTitle="Provider Search Filters">
      {searchClicked && (
        <div style={{ textAlign: 'right', margin: '0.5rem 1rem' }}>
          <ResetButton onClick={resetFilter} type="button">
            Clear
          </ResetButton>
        </div>
      )}
      <div
        style={{
          paddingLeft: '1rem',
          paddingRight: '1rem',
          height: searchClicked ? 'calc(100% - 3rem)' : 'calc(100% - 5rem)',
          overflowY: 'auto',
          overflowX: 'hidden',
          paddingTop: '0.5rem',
          paddingBottom: '2rem',
        }}
      >
        {selectedZipCode && selectedProviderType && searchClicked && (
          <>
            {displayPlanMessage && (
              <Typography style={{ fontStyle: 'italic', marginTop: '3%', paddingLeft: '2%' }}>
                {planMessage}
              </Typography>
            )}
            <SearchBar input={input} setInput={setInput} />
            <PlanSelectFilter />
          </>
        )}
        <ZipCodeEntry zipcodeValue={zipcodeValue} setZipCodeValue={setZipCodeValue} />
        {zipcodeValue && zipcodeValue.length === 5 && (
          <>
            <ProviderTypeFilter />
          </>
        )}
        {!searchClicked && (
          <Button
            variant="contained"
            sx={{
              width: '100%',
              marginTop: '1.75rem',
              padding: '0.75rem',
              fontWeight: 600,
              backgroundColor: '#f26522',
              color: '#fff',
              '&:hover': {
                backgroundColor: '#d35400',
              },
              '&:disabled': {
                backgroundColor: '#cccccc',
                color: '#666666',
              },
            }}
            onClick={handleSearch}
            disabled={isSearchDisabled}
          >
            Search
          </Button>
        )}
        {selectedZipCode && selectedProviderType && searchClicked && (
          <SliderSelector sliderValue={sliderValue} setSliderValue={setSliderValue} />
        )}
        {selectedZipCode && selectedProviderType && searchClicked && (
          <>
            <AccessibilityFilter />
            <SortControl />
            <GenderFilter />
            <EntityTypeFilter />
          </>
        )}
      </div>
    </Sidebar>
  );
}

export default connect(mapStateToProps, mapDispatchToProps)(ProviderSearchFilterPane);
